import React, { useMemo, useState } from 'react';
import Icon from './Icon';
import { UnifiedBundle } from '../types/Product';
import { FIXED_RATES_DATE_LABEL } from '../utils/currency';
import { absDecimal, compareDecimal, formatDecimal } from '../utils/decimal';
import { convertDecimalAtFixedRate } from '../utils/fixedRateConvert';
import { useMoney } from '../hooks/useMoney';
import { useRole } from '../hooks/useRole';
import { useBundleSourcedUnits } from '../hooks/useBundleSourcedUnits';
import {
  computeBundleEconomics,
  describeNet,
  summariseNet,
  EconomicsRow,
} from '../utils/bundleEconomics';
import '../styles/components/BundleEconomicsPanel.css';
import '../styles/components/DataTable.css';

interface BundleEconomicsPanelProps {
  bundles: readonly UnifiedBundle[];
}

/** "+£36.40" / "-£12.00" / "£0.00", in the project currency. */
const signed = (amount: string, currency: string): string => {
  const sign = compareDecimal(amount, '0');
  return `${sign === -1 ? '-' : sign === 1 ? '+' : ''}${formatDecimal(absDecimal(amount), currency)}`;
};

const signedPct = (pct: number): string => `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;

/** State is always spelled out in words, never carried by colour alone. */
const StatePill: React.FC<{ state: 'margin' | 'subsidy' | 'breakeven' }> = ({ state }) => {
  const variant = state === 'margin' ? 'success' : state === 'subsidy' ? 'warning' : 'neutral';
  const label = state === 'margin' ? 'Margin' : state === 'subsidy' ? 'Subsidy' : 'Break even';
  return <span className={`ds-badge ds-badge-${variant}`}>{label}</span>;
};

const isCompared = (row: EconomicsRow) =>
  row.state === 'margin' || row.state === 'subsidy' || row.state === 'breakeven';

const costCell = (row: EconomicsRow, currency: string): React.ReactNode => {
  if (row.state === 'needs-price') return <span className="bec-muted">n/a</span>;
  if (row.state === 'needs-cost') return <span className="bec-muted">Needs a target cost</span>;
  if (row.state === 'not-comparable') return `${row.cost} ${row.costCurrency}`;
  if (row.convertedCost === null) return formatDecimal(row.cost, row.costCurrency);
  return (
    <>
      {formatDecimal(row.cost, row.costCurrency)}
      <span className="bec-sub">
        about {formatDecimal(row.convertedCost, currency)} at the app&apos;s fixed rate, {FIXED_RATES_DATE_LABEL}
      </span>
    </>
  );
};

/** Free bundles are not sold, so they stay out; only an asoebi bundle with
 *  no price is shown, as "Needs a price". */
const belongsInPanel = (b: UnifiedBundle): boolean =>
  b.is_active && !b.archived && !b.deletedAt && (b.price > 0 || b.types.includes('asoebi'));

const marginCell = (row: EconomicsRow, currency: string): React.ReactNode => {
  if (row.state === 'needs-price') return <span className="bec-muted">Needs a price</span>;
  if (row.state === 'needs-cost') return <span className="bec-muted">n/a</span>;
  if (row.state === 'not-comparable') {
    return <span className="bec-muted">Cost in {row.costCurrency}, not compared</span>;
  }
  return (
    <span className="bec-amount">
      <StatePill state={row.state as 'margin' | 'subsidy' | 'breakeven'} />
      <span>{signed(row.marginPerUnit as string, currency)}</span>
    </span>
  );
};

/**
 * Whether each bundle makes a margin or needs a subsidy, and whether the
 * margin on one covers the subsidy on another. Cost is the bundle's target
 * (a sourcing ceiling, not an actual). Planned units are a what-if kept in
 * this component's state only; the true unit counts live on procurement
 * order lines.
 */
const EconomicsTable: React.FC<BundleEconomicsPanelProps & { currency: string }> = ({ bundles, currency }) => {
  const economicsContext = useMemo(
    () => ({ currency, convert: (amount: string, from: string) => convertDecimalAtFixedRate(amount, from, currency) }),
    [currency],
  );
  const [units, setUnits] = useState<Record<string, string>>({});
  const [collapsed, setCollapsed] = useState(false);

  const eligible = useMemo(() => bundles.filter(belongsInPanel), [bundles]);
  const comparableIds = useMemo(
    () => computeBundleEconomics(eligible, {}, economicsContext)
      .filter(isCompared)
      .map(r => r.id),
    [eligible, economicsContext],
  );
  const { sourced, loading } = useBundleSourcedUnits(comparableIds);

  // What the table uses: the user's own number if they typed one, else the
  // units already sourced on order lines.
  const effectiveUnits = useMemo(() => {
    const merged: Record<string, string> = {};
    Object.keys(sourced).forEach(id => { merged[id] = String(sourced[id]); });
    return { ...merged, ...units };
  }, [sourced, units]);

  const rows = useMemo(
    () => computeBundleEconomics(eligible, effectiveUnits, economicsContext),
    [eligible, effectiveUnits, economicsContext],
  );
  const summary = useMemo(() => summariseNet(rows), [rows]);
  const sentence = describeNet(summary, amount => formatDecimal(amount, currency));

  if (rows.length === 0) return null;

  const setUnitsFor = (id: string, value: string) =>
    setUnits(prev => ({ ...prev, [id]: value }));
  const resetUnits = (id: string) =>
    setUnits(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== id)));

  return (
    <section className="bec-panel" aria-labelledby="bec-heading" aria-busy={loading}>
      <button
        type="button"
        className="bec-header"
        onClick={() => setCollapsed(c => !c)}
        aria-expanded={!collapsed}
      >
        <h3 id="bec-heading">Bundle economics</h3>
        <Icon name={collapsed ? 'chevronDown' : 'chevronUp'} size={16} />
      </button>

      {!collapsed && (
        <>
          <p className="bec-hint">
            Target cost is a sourcing target, not an actual. Planned units are a what-if for this
            visit only and are not saved. Units start from what is already sourced on order lines.
            {loading && <span className="bec-loading"> Loading order lines</span>}
          </p>
          <div className="dt-wrap">
            <div className="dt-scroll ds-scroll-x ds-scroll-x--framed">
              <table className="dt dt--compact">
                <thead>
                  <tr>
                    <th>Bundle</th>
                    <th className="bec-num">Price per unit</th>
                    <th className="bec-num">Target cost per unit</th>
                    <th className="bec-num">Margin per unit</th>
                    <th className="bec-num">Margin %</th>
                    <th className="bec-num">Planned units</th>
                    <th className="bec-num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row.id}>
                      <td><strong>{row.name}</strong></td>
                      <td className="bec-num">
                        {row.price === null ? <span className="bec-muted">n/a</span> : formatDecimal(row.price, currency)}
                      </td>
                      <td className="bec-num">{costCell(row, currency)}</td>
                      <td className="bec-num">{marginCell(row, currency)}</td>
                      <td className="bec-num">
                        {row.marginPct === null ? <span className="bec-muted">n/a</span> : signedPct(row.marginPct)}
                      </td>
                      <td className="bec-num">
                        {isCompared(row) ? (
                          <>
                            <input
                              type="number"
                              className="form-input form-input--sm bec-units"
                              min={0}
                              step={1}
                              inputMode="numeric"
                              aria-label={`Planned units for ${row.name}`}
                              value={effectiveUnits[row.id] ?? ''}
                              onChange={e => setUnitsFor(row.id, e.target.value)}
                            />
                            {row.id in units ? (
                              <span className="bec-source">
                                Your number
                                {row.id in sourced && (
                                  <button type="button" className="bec-reset" onClick={() => resetUnits(row.id)}>
                                    Reset to order lines
                                  </button>
                                )}
                              </span>
                            ) : row.id in sourced ? (
                              <span className="bec-source">From order lines</span>
                            ) : null}
                          </>
                        ) : (
                          <span className="bec-muted">n/a</span>
                        )}
                      </td>
                      <td className="bec-num">
                        {row.total === null ? <span className="bec-muted">n/a</span> : signed(row.total, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {summary.net !== null && (
                  <tfoot>
                    <tr className="bec-net-row">
                      <th scope="row" colSpan={6}>Net across selected bundles</th>
                      <td className="bec-num">
                        <span className="bec-amount">
                          <StatePill state={compareDecimal(summary.net, '0') === 1 ? 'margin' : compareDecimal(summary.net, '0') === -1 ? 'subsidy' : 'breakeven'} />
                          <strong>{signed(summary.net, currency)}</strong>
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
          <p className="bec-sentence" role="status">{sentence}</p>
        </>
      )}
    </section>
  );
};

/**
 * Prices, costs and margins are money: not for a contractor or anyone flagged
 * financially blind, and not until the project has a currency to show them in.
 * The gate sits outside the table so a hidden panel also skips its order-line
 * fetches.
 */
const BundleEconomicsPanel: React.FC<BundleEconomicsPanelProps> = ({ bundles }) => {
  const { canViewFinancials } = useRole();
  const { isSet, currency } = useMoney();
  if (!canViewFinancials || !isSet) return null;
  return <EconomicsTable bundles={bundles} currency={currency} />;
};

export default BundleEconomicsPanel;
